import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, AlertTriangle, Download, FileCheck2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import Swal from 'sweetalert2';
import StatusBadge from '../../Common/StatusBadge';
import { useFeatures } from '../../../../context/FeatureContext';
import { useCapabilities } from '../../../../context/CapabilityContext';
import { addToQueue, apiError, downloadFileContent, fetchDebtorAccounts, fetchFiles, fetchQueue, markUploaded, removePayrollFromQueue } from '../../Banking/payments/paymentsApi';
import { FILE_STATUS, kr } from '../../Banking/payments/paymentStatus';

/**
 * Payroll step 2 → "Löneutbetalning" action card (ISO 20022 payment file).
 * Locking the payroll queues the net salaries when the company has a bank
 * account enabled for payment files. This card shows, for THIS payroll
 * (matched on payroll_id, never on dates — the file's payout date is
 * bank-day adjusted): every live file, and every row still in the queue with
 * its problems, so a partial file never hides the salaries that are left.
 * It offers the explicit queue path for payrolls locked before the account
 * was enabled, "Betalas på annat sätt" for a payroll paid in the internet
 * bank instead, and links to the Betalningar page to assemble a file.
 * Replaces the Bankgiro (KI) file the banks retired in 2026.
 *
 * Payment files are admin-only and entitlement-gated: without either, the
 * card renders nothing and the salaries are paid from the manual payout table.
 */
const LIVE_FILE = (f) => !['cancelled', 'rejected'].includes(f.status);

const SalaryPaymentCard = ({ companyId, currentPayroll, disabled }) => {
  const { features } = useFeatures();
  const { capabilities } = useCapabilities();
  const available = Boolean(features?.payment_files) && Boolean(capabilities?.isAdmin);

  const [rows, setRows] = useState([]);
  const [files, setFiles] = useState([]);
  const [accountEnabled, setAccountEnabled] = useState(true);
  const [denied, setDenied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const payrollId = currentPayroll?.id || null;
  const isLocked = Boolean(currentPayroll?.isLocked);

  const load = useCallback(async () => {
    if (!available || !isLocked || !payrollId) { setRows([]); setFiles([]); setLoaded(true); return; }
    try {
      const [q, list, accounts] = await Promise.all([fetchQueue(companyId), fetchFiles(companyId, { category: 'SALA' }), fetchDebtorAccounts(companyId)]);
      setRows(q.items.filter((i) => i.source_type === 'payroll' && i.payroll_id === payrollId));
      setFiles(list.filter((f) => f.payroll_id === payrollId && LIVE_FILE(f)));
      setAccountEnabled(accounts.length > 0);
      setDenied(false);
    } catch (err) {
      // The server says no although the client thought yes (role or plan changed): offer nothing.
      if (err?.response?.status === 403) setDenied(true);
      else toast.error(apiError(err, 'Kunde inte hämta betalningskön.').message);
    } finally {
      setLoaded(true);
    }
  }, [available, companyId, isLocked, payrollId]);

  useEffect(() => { load(); }, [load]);

  const payOutsideFile = async () => {
    const answer = await Swal.fire({
      title: 'Betalas lönerna på annat sätt?',
      text: 'Lönerna tas bort från betalningskön. Du betalar dem själv i internetbanken.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Ja, ta bort från kön',
      cancelButtonText: 'Avbryt',
    });
    if (!answer.isConfirmed) return;
    setBusy(true);
    try {
      const result = await removePayrollFromQueue(companyId, payrollId);
      toast.success(`${result.removed} löner togs bort från betalningskön.`);
      await load();
    } catch (err) {
      toast.error(apiError(err, 'Kunde inte ta bort lönerna från betalningskön.').message);
    } finally {
      setBusy(false);
    }
  };

  const queueSalaries = async () => {
    const answer = await Swal.fire({
      title: 'Har lönerna för perioden redan betalats ut?',
      text: 'Lägg bara lönerna i betalningskön om de inte redan är utbetalda från banken.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Nej, lägg i betalningskön',
      cancelButtonText: 'Ja, redan utbetalda',
    });
    if (!answer.isConfirmed) return;
    setBusy(true);
    try {
      const result = await addToQueue(companyId, { sourceType: 'payroll', sourceIds: [payrollId], confirmed: true });
      const alreadyPaid = (result.skipped || []).filter((s) => /betald/i.test(s.reason || '')).length;
      toast.success(`${result.queued} löner lades i betalningskön.${alreadyPaid ? ` ${alreadyPaid} är redan betalda via betalfil och lades inte till.` : ''}`);
      await load();
    } catch (err) {
      toast.error(apiError(err, 'Kunde inte lägga lönerna i betalningskön.').message);
    } finally {
      setBusy(false);
    }
  };

  // One file action at a time: a second click while the first is on its way is ignored.
  const runFileAction = async (action, success) => {
    if (busy) return;
    setBusy(true);
    try {
      await action();
      if (success) toast.success(success);
      await load();
    } catch (err) {
      toast.error(apiError(err).message);
    } finally {
      setBusy(false);
    }
  };

  const total = rows.reduce((s, r) => s + Number(r.amount), 0);
  const invalid = rows.filter((r) => !r.valid);
  const hasValidRows = rows.some((r) => r.valid);
  const ready = isLocked && loaded;

  if (!available || denied) return null;

  return (
    <div className="action-card">
      <div className="action-card-header">
        <div className="action-card-icon">
          <FileCheck2 size={20} />
        </div>
        <h4 className="action-card-title">Löneutbetalning</h4>
      </div>
      <p className="action-card-description">
        Skapa en betalfil (ISO 20022) med nettolönerna och ladda upp den i internetbanken för signering.
      </p>

      {ready && files.map((file) => {
        const status = FILE_STATUS[file.status] || { label: file.status, tone: 'muted' };
        return (
          <div key={file.batch_id} style={{ fontSize: 13, marginBottom: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <StatusBadge tone={status.tone}>{status.label}</StatusBadge>
              <span>{file.label} · {file.instruction_count} st · {kr(file.total_amount)} · dras {file.requested_execution_date}</span>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
              <button type="button" className="ll-btn ll-btn--secondary ll-btn--sm" disabled={busy} onClick={() => runFileAction(() => downloadFileContent(companyId, file))}>
                <Download size={14} /> Ladda ner
              </button>
              {file.status === 'created' && (
                <button type="button" className="ll-btn ll-btn--primary ll-btn--sm" disabled={busy} onClick={() => runFileAction(() => markUploaded(companyId, file.batch_id), 'Filen är markerad som uppladdad.')}>
                  <Upload size={14} /> Markera som uppladdad
                </button>
              )}
              <Link className="ll-btn ll-btn--ghost ll-btn--sm" to={`/${companyId}/payments?tab=files&file=${file.batch_id}`}>Visa filen</Link>
            </div>
          </div>
        );
      })}

      {ready && rows.length > 0 && (
        <div style={{ fontSize: 13, marginBottom: 10 }}>
          <div>
            {files.length > 0 ? 'Kvar i betalningskön: ' : ''}{rows.length} löner · <strong>{kr(total)}</strong>
          </div>
          {invalid.length > 0 && (
            <div className="text-danger" style={{ marginTop: 4 }}>
              <AlertCircle size={12} className="me-1" />
              {invalid.length} saknar giltiga kontouppgifter: {invalid.map((r) => r.label).join(', ')}
            </div>
          )}
          {rows.some((r) => r.valid && r.warnings.length > 0) && (
            <div style={{ color: '#b45309', marginTop: 4 }}>
              <AlertTriangle size={12} className="me-1" />
              {rows.filter((r) => r.valid && r.warnings.length > 0).map((r) => r.label).join(', ')}: kontrollera kontouppgifterna
            </div>
          )}
        </div>
      )}

      {ready && rows.length === 0 && files.length === 0 && !accountEnabled && (
        <div style={{ fontSize: 13, marginBottom: 10 }}>
          Inget bankkonto är aktiverat för betalfiler, så lönerna betalas i internetbanken.{' '}
          <Link to={`/${companyId}/settings/bank-connections`}>Aktivera betalfiler</Link> för att skapa en lönefil här.
        </div>
      )}

      {ready && rows.length === 0 && files.length === 0 && accountEnabled && (
        <button type="button" className="action-button btn-primary-action" onClick={queueSalaries} disabled={disabled || busy}>
          <FileCheck2 size={16} />
          Lägg löner i betalningskön
        </button>
      )}

      {ready && rows.length > 0 && (
        <Link
          className="action-button btn-primary-action"
          to={`/${companyId}/payments?select=sala`}
          style={disabled || !hasValidRows ? { pointerEvents: 'none', opacity: 0.6 } : undefined}
          title={hasValidRows ? undefined : 'Rätta kontouppgifterna innan en betalfil kan skapas'}
        >
          <FileCheck2 size={16} />
          {files.length > 0 ? 'Skapa betalfil för resterande' : 'Skapa betalfil'}
        </Link>
      )}

      {ready && rows.length > 0 && (
        <button type="button" className="ll-btn ll-btn--ghost ll-btn--sm" style={{ marginTop: 8 }} onClick={payOutsideFile} disabled={disabled || busy}>
          Betalas på annat sätt
        </button>
      )}

      {!isLocked && (
        <button type="button" className="action-button btn-primary-action" disabled>
          <FileCheck2 size={16} />
          Lås perioden först
        </button>
      )}
    </div>
  );
};

export default SalaryPaymentCard;
